import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { ArrowLeft, Download, Printer, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContractView = () => {
  const { reservationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [contract, setContract] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [accepting, setAccepting] = useState(false);
  
  const showAcceptOption = searchParams.get('aceitar') === 'true';

  useEffect(() => {
    loadContract();
  }, [reservationId]);

  const loadContract = async () => {
    try {
      setLoading(true);
      
      // Carregar contrato
      const response = await axios.get(`${BACKEND_URL}/api/contracts/reservation/${reservationId}`);
      setContract(response.data);
      
      // Carregar reserva para verificar se precisa aceitar
      try {
        const resResponse = await axios.get(`${BACKEND_URL}/api/reservations/${reservationId}`);
        setReservation(resResponse.data);
      } catch (e) {
        // Ignorar erro de reserva
      }
      
    } catch (err) {
      console.error('Erro ao carregar contrato:', err);
      setError('Não foi possível carregar o contrato.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptContract = async () => {
    if (!acceptTerms) {
      alert('Você precisa marcar que leu e aceita os termos do contrato.');
      return;
    }
    
    try {
      setAccepting(true);
      await axios.post(`${BACKEND_URL}/api/contracts/accept-by-reservation/${reservationId}`);
      alert('Contrato aceito com sucesso!');
      navigate('/minha-conta');
    } catch (err) {
      console.error('Erro ao aceitar contrato:', err);
      alert('Erro ao aceitar contrato. Tente novamente.');
    } finally {
      setAccepting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    if (contract?.content) {
      const blob = new Blob([`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Contrato - ${contract.package_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
            .contract { max-width: 800px; margin: 0 auto; }
            h1 { color: #003366; text-align: center; }
            .signature { margin-top: 60px; text-align: center; }
          </style>
        </head>
        <body>
          ${contract.content}
        </body>
        </html>
      `], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Contrato_${contract.package_name?.replace(/\s/g, '_')}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#003366] mx-auto mb-4" />
          <p className="text-gray-600">Carregando contrato...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={() => navigate('/minha-conta')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar para Minha Conta
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const isAlreadyAccepted = contract?.accepted || reservation?.contract_accepted;
  const needsAcceptance = showAcceptOption && !isAlreadyAccepted;

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header com ações */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4 print:hidden">
          <Button 
            variant="outline" 
            onClick={() => navigate('/minha-conta')}
            className="w-fit"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
            <Button onClick={handleDownload} className="bg-[#003366] hover:bg-[#002244]">
              <Download className="w-4 h-4 mr-2" />
              Baixar
            </Button>
          </div>
        </div>

        {/* Alerta de aceite pendente */}
        {needsAcceptance && (
          <Card className="mb-6 border-yellow-300 bg-yellow-50 print:hidden">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-yellow-800">Aceite de Contrato Pendente</p>
                  <p className="text-sm text-yellow-700">
                    Esta reserva foi feita manualmente. Por favor, leia o contrato completo abaixo e aceite os termos para confirmar sua participação na viagem.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Card do contrato */}
        <Card className="print:shadow-none">
          <CardContent className="p-4 md:p-8">
            {/* Status do contrato - já aceito */}
            {isAlreadyAccepted && (
              <div className="flex items-center gap-2 mb-6 p-3 bg-green-50 rounded-lg border border-green-200 print:hidden">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-green-800 font-medium">
                  Contrato aceito em {contract?.accepted_at ? new Date(contract.accepted_at).toLocaleDateString('pt-BR') : new Date(contract?.created_at).toLocaleDateString('pt-BR')}
                </span>
              </div>
            )}

            {/* Informações resumidas */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg print:bg-white print:border">
              <div>
                <p className="text-xs text-gray-500">Pacote</p>
                <p className="font-medium text-sm">{contract?.package_name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Data da Viagem</p>
                <p className="font-medium text-sm">{contract?.travel_date}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Contratante</p>
                <p className="font-medium text-sm">{contract?.customer_name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">CPF</p>
                <p className="font-medium text-sm">{contract?.customer_cpf}</p>
              </div>
            </div>

            {/* Conteúdo do contrato */}
            <div 
              className="prose max-w-none text-sm"
              dangerouslySetInnerHTML={{ __html: contract?.content || '' }}
            />
          </CardContent>
        </Card>

        {/* Seção de aceite */}
        {needsAcceptance && (
          <Card className="mt-6 border-[#003366] print:hidden">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="accept-terms"
                    checked={acceptTerms}
                    onCheckedChange={setAcceptTerms}
                    className="mt-1"
                  />
                  <label htmlFor="accept-terms" className="text-sm text-gray-700 cursor-pointer">
                    <strong>Declaro que li e aceito</strong> todos os termos e condições deste contrato de prestação de serviços turísticos. 
                    Estou ciente das políticas de cancelamento, reembolso e das obrigações como contratante.
                  </label>
                </div>
                
                <Button 
                  onClick={handleAcceptContract}
                  disabled={!acceptTerms || accepting}
                  className="w-full bg-[#003366] hover:bg-[#002244] text-white py-6 text-lg"
                >
                  {accepting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Aceitar Contrato
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ContractView;
